import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { localizationService } from '@shared-services/localization-service';
import { Div, Span } from 'client/widget-components/basicComponents';
import { Input } from 'client/widget-components/basicComponents/Input/Input';
import { StyledButton } from 'client/widget-components/basicComponents/StyledButton';
import Text, {
    TextDomTag,
} from 'client/widget-components/basicComponents/Text';
import FieldError from 'client/widget-components/components/FieldError';
import { getCurrencySymbol } from 'client/widget-components/ecom/stores/customAmount';
import CustomAmountState from 'client/widget-components/ecom/stores/CustomAmountState';
import {
    mergeResponsiveStyles,
    nestResponsiveStyles,
    ResponsiveStylesProp,
} from 'client/widget-components/responsive-styles/responsiveStylesService';
import { useEcommL10n } from 'client/widgets/runtime-order-confirmation/useEcommStr';
import { observer } from 'mobx-react';
import React, { useEffect, useId } from 'react';
import {
    CUSTOM_AMOUNT_PICKER_DATA_AUTO,
    formatCustomAmountNotice,
    getCustomAmountNotice,
} from './customAmountPickerUtils';
import {
    CustomAmountTitle,
    PriceOptionsStyles,
} from './productPriceOptionsTypes';

const { str } = localizationService;

interface CustomAmountPickerProps {
    customAmount: CustomAmountState;
    title?: CustomAmountTitle;
    styles?: PriceOptionsStyles;
    containerStyles?: ResponsiveStylesProp;
}

const CustomAmountPicker = ({
    customAmount,
    title,
    styles,
    containerStyles,
}: CustomAmountPickerProps) => {
    const id = useId();
    // Tells this product's add-to-cart that the shopper can see the picker.
    useEffect(() => customAmount.attachPicker(), [customAmount]);
    const titleId = `${id}title`;
    const errorId = `${id}error`;
    const l10n = useEcommL10n();
    const { config, input, selectedIndex } = customAmount;
    const notice = getCustomAmountNotice(customAmount);
    const errorMessage = notice ? formatCustomAmountNotice(notice, l10n) : null;
    const showTitle = title?.isVisible !== false;
    const placeholder = str('rt.productPriceOptions.customAmount.placeholder');
    const placeholderStyles = mergeResponsiveStyles([
        placeholderDefaultStyles,
        styles?.customAmountPlaceholder,
    ]);

    return (
        <Div
            data-auto={CUSTOM_AMOUNT_PICKER_DATA_AUTO}
            styles={[pickerDefaultStyles, containerStyles]}
        >
            {showTitle && (
                <Text
                    tag={TextDomTag.h4}
                    grabId='custom-amount-title'
                    domAttrs={{ id: titleId }}
                    styles={[titleDefaultStyles, styles?.customAmountTitle]}
                >
                    {title?.text ||
                        str('rt.productPriceOptions.customAmount.title')}
                </Text>
            )}

            {config.suggestedAmounts.length > 0 && (
                <Div
                    role='group'
                    aria-labelledby={showTitle ? titleId : undefined}
                    data-auto='custom-amount-options'
                    data-grab='custom-amount-options'
                    styles={[optionsDefaultStyles, styles?.customAmountOptions]}
                >
                    {config.suggestedAmounts.map(
                        ({ amount, displayed_amount }, index) => {
                            const isSelected = index === selectedIndex;
                            return (
                                <StyledButton
                                    key={`${index}-${amount}`}
                                    aria-pressed={isSelected}
                                    data-auto='custom-amount-option'
                                    data-grab='custom-amount-option'
                                    styles={[
                                        optionDefaultStyles,
                                        styles?.customAmountOption,
                                        isSelected && [
                                            optionSelectedDefaultStyles,
                                            styles?.customAmountOptionSelected,
                                        ],
                                    ]}
                                    onClick={() =>
                                        customAmount.selectSuggestion(index)
                                    }
                                >
                                    <Span
                                        data-grab='custom-amount-option-text'
                                        styles={
                                            isSelected
                                                ? [
                                                      optionSelectedTextDefaultStyles,
                                                      styles?.customAmountOptionSelectedText,
                                                  ]
                                                : styles?.customAmountOptionText
                                        }
                                    >
                                        {displayed_amount}
                                    </Span>
                                </StyledButton>
                            );
                        }
                    )}
                </Div>
            )}

            {config.allowCustomAmount && (
                <Div
                    data-grab='custom-amount-field'
                    styles={[
                        fieldDefaultStyles,
                        styles?.customAmountField,
                        !!notice && fieldErrorStyles,
                    ]}
                >
                    <Input
                        type='text'
                        inputMode={config.decimals ? 'decimal' : 'numeric'}
                        autoComplete='off'
                        data-auto='custom-amount-input'
                        data-grab='custom-amount-input'
                        value={input}
                        placeholder={placeholder}
                        aria-label={placeholder}
                        aria-invalid={!!notice}
                        aria-describedby={errorMessage ? errorId : undefined}
                        onChange={(event) =>
                            customAmount.setInput(event.target.value)
                        }
                        onBlur={() => customAmount.validate()}
                        styles={[
                            inputDefaultStyles,
                            styles?.customAmountInput,
                            nestResponsiveStyles(placeholderStyles, {
                                innerSelector: '&::placeholder',
                            }),
                        ]}
                        increaseSpecificity
                    />
                    <Span
                        aria-hidden
                        data-auto='custom-amount-currency'
                        styles={currencyDefaultStyles}
                    >
                        {getCurrencySymbol(config.format, config.currency)}
                    </Span>
                </Div>
            )}

            {errorMessage && (
                <FieldError
                    error={errorMessage}
                    domAttrs={{ id: errorId, role: 'alert' }}
                    styles={errorDefaultStyles}
                />
            )}
        </Div>
    );
};

const pickerDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
};

const titleDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        margin: 0,
    },
};

// "Number of options per line" is this container's grid-template-columns.
const optionsDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 8,
    },
};

const optionDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        minHeight: 36,
        padding: '8px 12px',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'center',
        overflowWrap: 'anywhere',
    },
};

const optionSelectedDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
};

const optionSelectedTextDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        fontWeight: 600,
    },
};

const fieldDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        alignItems: 'stretch',
        border: '1px solid #8A8A8A',
        borderRadius: 3,
        overflow: 'hidden',
    },
};

const fieldErrorStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        borderColor: '#B00020',
    },
};

const inputDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        flex: 1,
        minWidth: 0,
        padding: '8px 12px',
        color: 'inherit',
        font: 'inherit',
    },
};

const placeholderDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        color: '#8A8A8A',
        opacity: 1,
    },
};

const currencyDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        borderInlineStart: '1px solid #8A8A8A',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
};

// Same inline error as the product customizations.
const errorDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        borderRadius: '8px',
        padding: '6px 12px',
        gap: '4px',
        backgroundColor: '#FEF5F5',
        color: '#B00020',
        minHeight: '32px',
        fontSize: '13px',
        lineHeight: '20px',
    },
};

export default observer(CustomAmountPicker);
